import { Link } from '@tanstack/react-router'
import { ArrowLeft, CalendarDays } from 'lucide-react'
import { useState } from 'react'
import {
  MetricCombobox,
  type MetricComboboxOption,
} from '@/components/custom/metrics/metric-combobox'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import type { CompanyReview } from '@/features/actual-vs-budget/company-review'
import { CompanySummary } from '@/components/custom/actual-vs-budget/company-summary'
import { CompanyTrajectoryChart } from '@/components/custom/actual-vs-budget/company-trajectory-chart'
import { PlReviewTable } from '@/components/custom/actual-vs-budget/pl-review-table'
import type { ActualVsBudgetMetricKey } from '@/features/actual-vs-budget/types'

function isMetricKey(value: string): value is ActualVsBudgetMetricKey {
  return value === 'revenue' || value === 'adjusted-ebitda'
}

const metricOptions: MetricComboboxOption[] = [
  { id: 'revenue', label: 'Revenue' },
  { id: 'adjusted-ebitda', label: 'Adjusted EBITDA' },
]

export function CompanyReviewView({ review }: { review: CompanyReview }) {
  const [selectedMetric, setSelectedMetric] =
    useState<ActualVsBudgetMetricKey>('revenue')
  const metric = review.metrics[selectedMetric]

  return (
    <div className="space-y-6">
      <Button asChild variant="ghost" className="-ml-3 w-fit gap-2">
        <Link
          to="/experiments/actual-vs-budget"
          search={{ company: undefined }}
        >
          <ArrowLeft aria-hidden="true" className="size-4" />
          Back to portfolio
        </Link>
      </Button>

      <section className="rounded-lg border border-primary/10 bg-muted/20 p-5">
        <div className="flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
          <div>
            <Typography
              variant="body-sm"
              className="font-semibold uppercase tracking-[0.14em] text-muted-foreground"
            >
              Company financial review
            </Typography>
            <Typography as="h2" variant="h3" className="mt-1">
              {review.name}
            </Typography>
            <Typography variant="muted" className="mt-2">
              {review.fund} · {review.fiscalYearLabel}
            </Typography>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
              <Typography
                variant="body-sm"
                className="inline-flex items-center gap-1.5 text-primary/70"
              >
                <CalendarDays aria-hidden="true" className="size-3.5" />
                Fiscal year end {review.fiscalYearEndLabel}
              </Typography>
              <Typography variant="body-sm" className="text-primary/70">
                Latest closed month {review.latestClosedPeriodLabel}
              </Typography>
            </div>
          </div>

          <div
            className="w-full sm:w-60"
            role="group"
            aria-labelledby="company-review-metric-label"
          >
            <Typography
              id="company-review-metric-label"
              variant="body-sm"
              className="mb-1.5 font-medium text-primary/70"
            >
              Review metric
            </Typography>
            <MetricCombobox
              options={metricOptions}
              value={selectedMetric}
              onValueChange={(value) => {
                if (isMetricKey(value)) setSelectedMetric(value)
              }}
              groupBy="none"
              searchPlaceholder="Search review metrics..."
              emptyText="No review metrics found."
              triggerClassName="bg-white"
            />
          </div>
        </div>
      </section>

      <CompanySummary summary={metric.summary} />

      <CompanyTrajectoryChart metric={metric} />

      <PlReviewTable review={review.plReview} />
    </div>
  )
}
